import { Badge } from '@monksflow/monks-ui';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import { LockIcon, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AGENT_VISIBILITY_ICONS } from '@/shared/ui';

import type { WorkspaceAccess } from '../../model/tools.types';
import { WorkspaceChipList } from './WorkspaceChipList';

interface WorkspaceAccessCellProps {
  workspaceAccess: WorkspaceAccess;
  workspaces: WorkspaceResponseDto[];
  /** Reachable by its author alone, shown in place of the access modes. */
  isPrivate?: boolean;
}

function ReachBadge({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <Badge variant="secondary" size="sm" roundness="full">
      <Icon aria-hidden="true" />
      {label}
    </Badge>
  );
}

export function WorkspaceAccessCell({
  workspaceAccess,
  workspaces,
  isPrivate = false,
}: WorkspaceAccessCellProps) {
  const { t } = useTranslation('admin');

  if (isPrivate) {
    return <ReachBadge icon={LockIcon} label={t('admin.workspaceAccessChips.private')} />;
  }

  if (workspaceAccess === 'all') {
    return (
      <ReachBadge
        icon={AGENT_VISIBILITY_ICONS.org}
        label={t('admin.workspaceAccessChips.organization')}
      />
    );
  }

  if (workspaceAccess === 'none') {
    return (
      <span className="text-body-md text-destructive">
        {t('admin.workspaceAccessChips.noWorkspaces')}
      </span>
    );
  }

  // An allow or deny list that names nobody: the mode is set but says nothing yet.
  if (workspaces.length === 0) {
    return (
      <span className="text-body-md text-muted-foreground">
        {t('admin.workspaceAccessChips.none')}
      </span>
    );
  }

  return workspaceAccess === 'only' ? (
    <WorkspaceChipList
      workspaces={workspaces}
      variant="positive"
      label={t('admin.workspaceAccessChips.onlyInWorkspace')}
    />
  ) : (
    <WorkspaceChipList
      workspaces={workspaces}
      variant="destructive"
      label={t('admin.workspaceAccessChips.allWorkspacesExcept')}
    />
  );
}
